/**
 * GlassButton Component - Web Implementation
 *
 * Floating Action Button (FAB) with gradient variants.
 * Uses pure CSS gradient for web platform (LinearGradient doesn't render on React Native Web).
 *
 * Features:
 * - 4 gradient variants (brand, theatrical, primary, accent)
 * - Two-layer shadow for depth
 * - Pill shape (fully rounded borders)
 * - Press animation (scale 0.98)
 * - Disabled state with reduced opacity
 * - WCAG AA: White text on gradient background
 * - NO hover slide animation (removed per requirement)
 * - Clapper board icon (screenwriting metaphor: "rewrite your life story")
 *
 * Gradient Variants (for future brand identity research):
 * - brand: Pink→Cyan (logo/branding aesthetic)
 * - theatrical: Pink→Purple (entertainment/show mode)
 * - primary: Purple→Blue (default, main CTAs)
 * - accent: Purple→Teal (alternative/i18n variant)
 *
 * @example
 * <GlassButton variant="primary" onPress={handleSubmit} showIcon>
 *   Analyze Message
 * </GlassButton>
 */

import React from 'react';
import { Pressable, Text, PressableProps, StyleProp, ViewStyle, View } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

/**
 * Button gradient variants mapped from design references (button-bg-1 through button-bg-4)
 * CSS linear-gradient format for web rendering
 * Preserved for future brand identity research and A/B testing
 */
const BUTTON_GRADIENTS = {
  brand: 'linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)', // Pink→Cyan (button-bg-1)
  theatrical: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)', // Pink→Purple (button-bg-2)
  primary: 'linear-gradient(135deg, #b794f4 0%, #63b3ed 100%)', // Purple→Blue (button-bg-3)
  accent: 'linear-gradient(135deg, #a855f7 0%, #14b8a6 100%)', // Purple→Teal (button-bg-4)
} as const;

export type GlassButtonVariant = keyof typeof BUTTON_GRADIENTS;
export type GlassButtonSize = 'normal' | 'compact';

interface GlassButtonProps extends Omit<PressableProps, 'children'> {
  children: string;
  /** Gradient variant. Default: 'primary' */
  variant?: GlassButtonVariant;
  /** Button size. Default: 'normal' */
  size?: GlassButtonSize;
  /** Disabled state. Default: false */
  disabled?: boolean;
  /** Additional style for container */
  style?: StyleProp<ViewStyle>;
  /** Show clapper board icon (🎬 screenwriting metaphor). Default: false */
  showIcon?: boolean;
  /** Optional icon node rendered to the left of the text label. */
  iconLeft?: React.ReactNode;
}

export const GlassButton: React.FC<GlassButtonProps> = ({
  children,
  variant = 'primary',
  size = 'normal',
  disabled = false,
  style,
  onPress,
  showIcon = false,
  iconLeft,
  ...pressableProps
}) => {
  const shouldReduceMotion = useReducedMotion();
  const gradientBackground = BUTTON_GRADIENTS[variant];

  // Size-dependent styles
  const sizeStyles = size === 'compact' ? {
    paddingVertical: 12,
    paddingHorizontal: 24,
    minHeight: 44,
    minWidth: 200,
  } : {
    paddingVertical: 20,
    paddingHorizontal: 40,
    minHeight: 48,
    minWidth: 280,
  };

  const iconStyles = size === 'compact' ? {
    fontSize: '18px',
    lineHeight: '18px',
    marginTop: -1,
  } : {
    fontSize: '22px',
    lineHeight: '22px',
    marginTop: -2,
  };

  const textStyles = size === 'compact' ? {
    fontSize: '16px',
  } : {
    fontSize: '18px',
  };

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        // Pure CSS for web - matches prototype exactly
        // Increased padding for theatrical presence (button-bg-4.png)
        // 48px minHeight meets Material Design + WCAG AAA touch targets
        // Wider minWidth (280px) for better mobile presence and single-line text
        {
          ...sizeStyles,
          borderRadius: 9999, // Pill shape
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // Gradient variant applied from BUTTON_GRADIENTS
          background: gradientBackground,
          // Two-layer shadow for depth (matches prototype shadow-fab)
          boxShadow: '0 6px 10px 0 rgba(0, 0, 0, 0.4), 0 2px 6px 0 rgba(0, 0, 0, 0.3)',
          cursor: 'pointer',
          transition: 'all 250ms ease-out',
          border: 'none',
          outline: 'none',
        } as any,
        // Active press animation (matches prototype)
        // Web CSS transform uses string format, not React Native array syntax
        pressed && !shouldReduceMotion && { transform: 'scale(0.98)' } as any,
        // Disabled state
        disabled && { opacity: 0.5, cursor: 'not-allowed' } as any,
        style,
      ]}
      {...pressableProps}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        } as any}
      >
        {iconLeft}
        {showIcon && !iconLeft && (
          <Text
            style={{
              ...iconStyles,
            } as any}
          >
            🎬
          </Text>
        )}
        <Text
          style={{
            color: '#ffffff', // White text (WCAG AA on gradient background)
            ...textStyles, // text-base (matches prototype)
            fontWeight: 'bold', // Bold (matches prototype font-bold)
            textAlign: 'center',
            userSelect: 'none',
          } as any}
        >
          {children}
        </Text>
      </View>
    </Pressable>
  );
};
